import { z } from 'zod';
import { AudiencePolicy } from '../audience/index.js';
import { RecognizeAudienceSchema } from '../recognize/index.js';
import { parsePlainDate } from '../dates/index.js';
import type { Role } from '../permissions/types.js';
import { failure, success, type Result } from '../types.js';
import {
  SpotCashRangeSchema,
  SpotCommitteeSchema,
  SpotWorkflowConfigSchema,
} from '../spot/policy.js';
import { COPY_SWITCH_KEYS } from '../notifications/switches.js';
import { DEFAULT_SPOT_FUNDING, type SpotFunding } from '../funding/index.js';
import {
  DEFAULT_MATCH_RULES,
  DEFAULT_SYNC_FILTERS,
  firstSignInSchema,
  matchingSchema,
  syncFiltersSchema,
} from '../directory/sync-options.js';

export type SettingScope = 'system' | 'awardType' | 'pool' | 'sector' | 'person';
export interface SettingDefinition {
  readonly schema: z.ZodType;
  readonly default: unknown;
  readonly scope: SettingScope;
  readonly effectiveDated: boolean;
  readonly view: readonly Role[];
  readonly edit: readonly Role[];
  readonly reasonRequired: boolean;
  /**
   * Written whole, so every write through Settings says which saved copy it replaces (`basedOn`,
   * SENT-041/042): a write that doesn't is refused rather than set over a change it never saw.
   */
  readonly versionRequired?: boolean;
  readonly fixed?: boolean;
  readonly reasonOnDelete?: boolean;
  readonly managedOnAward?: boolean;
  /**
   * A customer input with no default at all (APP-096): until someone sets it, reads say it isn't
   * set (null) and anything that needs it refuses, rather than using a placeholder value.
   */
  readonly customerInput?: boolean;
  /**
   * May travel in a starting setup file's `settings` block and its Export (APP-118 D10): the
   * value holds no person IDs, isn't a secret, isn't an AI, Teams, mail or directory setting,
   * isn't managed on awards, and isn't `org.launchDate`. Keys with their own section in the file
   * (FMV, charge codes, the Executives' address, the splash, Preview access) aren't flagged: they
   * travel in that section. Checked by `portableSettingProblem` in the registry's tests.
   */
  readonly portable?: boolean;
}
const E: Role = 'executive',
  S: Role = 'stock_admin',
  F: Role = 'finance',
  P: Role = 'payroll',
  L: Role = 'sector_lead',
  D: Role = 'delegate',
  A: Role = 'system_admin';
const text = z.string().trim().min(1);
const count = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
/**
 * The `spot.fundingPool` setting (K37): the Spot Awards pool (APP-264, the default), the nominee's
 * sector pool, or one named pool. Every reader parses a saved value with this, never its own copy.
 */
export const SpotFundingSchema: z.ZodType<SpotFunding> = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('spot_pool') }).strict(),
  z.object({ kind: z.literal('nominee_sector') }).strict(),
  z.object({ kind: z.literal('fixed'), poolId: text }).strict(),
]);
const positive = count.min(1);
const rate = z
  .object({ num: count, den: positive })
  .strict()
  .refine((v) => v.num <= v.den);
const cashOptions = z.object({ cashCents: count, options: count }).strict();
const ohGa = z.object({ OH: text, GA: text }).strict();
const names = z.array(text);
const recordStrings = z.record(text, text);
/**
 * An https origin alone, such as `https://outlook.office365.us`: no path (not even a trailing
 * slash), query, fragment or user name, because links are made by adding a path to it.
 */
const httpsOrigin = z
  .url({ protocol: /^https$/, error: 'Use an https:// address.' })
  .regex(/^https:\/\/[a-z0-9.-]+(?::[0-9]{1,5})?$/i, 'Use the address alone, with no path.');
/**
 * APP-138 §D18, SENT-026: the Microsoft 365 web addresses Contact links may open, for commercial,
 * GCC High and DoD. Nothing else: a link opened with a sender's address must reach Microsoft.
 */
export const CONTACT_LINK_HOSTS = {
  outlook: [
    'outlook.office.com',
    'outlook.office365.com',
    'outlook.office365.us',
    'outlook-dod.office365.us',
    'webmail.apps.mil',
  ],
  teams: [
    'teams.microsoft.com',
    'teams.cloud.microsoft',
    'gov.teams.microsoft.us',
    'dod.teams.microsoft.us',
  ],
} as const;
/** One of these hosts, as an https origin alone: no port, user name, path, query or fragment. */
const microsoftOrigin = (hosts: readonly string[], error: string) =>
  httpsOrigin.refine((v) => hosts.some((h) => v.toLowerCase() === `https://${h}`), error);
export const AI_FEATURE_KEYS = [
  'help',
  'email',
  'teams',
  'ask_data',
  'feedback_summary',
  'feedback_voice',
  'help_voice',
  'feedback_assistant',
  'ask_summary',
] as const;
export type AiFeature = (typeof AI_FEATURE_KEYS)[number];
/**
 * The roles of the old "Executives, Sector Leads, delegates and requesters" choice, before an AI
 * feature's audience could name people (APP-139).
 */
export const AI_LEADER_ROLES = [
  'executive',
  'sector_lead',
  'delegate',
  'requester',
  'paa_taa_approver',
] as const satisfies readonly Role[];
/** The three choices an AI feature's audience had before APP-139, as stored values still hold them. */
const LEGACY_AI_AUDIENCES = {
  everyone: () => ({ everyone: true, roles: [], personIds: [] }),
  leaders: () => ({ everyone: false, roles: [...AI_LEADER_ROLES], personIds: [] }),
  executives: () => ({ everyone: false, roles: ['executive'], personIds: [] }),
} as const satisfies Record<string, () => AudiencePolicy>;
/**
 * Ask the data's audience on a database with nothing stored (APP-191 D15 A, D16): Executives, the
 * Stock Admin team and Sector Leads. Not a legacy key, so a stored `'executives'` still reads as
 * the Executives only. The audience narrows the permission and never widens it, so Sector Leads in
 * it change nothing until the permission lets them in.
 */
export const ASK_DATA_DEFAULT_AUDIENCE: AudiencePolicy = {
  everyone: false,
  roles: ['executive', 'stock_admin', 'sector_lead'],
  personIds: [],
};
/**
 * Who may use an AI feature, on top of the feature's own permission (SYS7 "Who can use it"):
 * APP-106's audience, so a feature can go to named people first (APP-139). A value saved before
 * then reads as the same people.
 */
export const aiAudience = z.preprocess(
  (value) =>
    typeof value === 'string' && Object.hasOwn(LEGACY_AI_AUDIENCES, value)
      ? LEGACY_AI_AUDIENCES[value as keyof typeof LEGACY_AI_AUDIENCES]()
      : value,
  AudiencePolicy,
);
const aiFeature = z
  .object({ on: z.boolean(), audience: aiAudience, model: text.nullable() })
  .strict();
/** A feature row as the registry's default and a later-added feature's default hold it: off, for everyone. */
const offForEveryone = (): AiFeatureSetting => ({
  on: false,
  audience: LEGACY_AI_AUDIENCES.everyone(),
  model: null,
});
/**
 * APP-263 (APP-191 D10 C): the Automated summary's default, off, for the Executives. It is the one
 * AI feature that sends figures (totals, never people's names), so it starts with the narrowest
 * audience.
 */
const offForExecutives = (): AiFeatureSetting => ({
  on: false,
  audience: LEGACY_AI_AUDIENCES.executives(),
  model: null,
});
export type AiFeatureSetting = z.infer<typeof aiFeature>;
/** The AI providers an environment may offer (APP-107): Anthropic Direct and AWS GovCloud (Bedrock). */
export const AI_PROVIDER_KINDS = ['anthropic', 'bedrock_govcloud'] as const;
export type AiProviderKind = (typeof AI_PROVIDER_KINDS)[number];
/**
 * Bedrock's regions (APP-107 §2.2). GovCloud always; the commercial ones only where the deployment
 * allows them (`AWS_COMMERCIAL_REGIONS_ALLOWED`), which the server checks: the domain can't know.
 */
export const AWS_GOV_REGIONS = ['us-gov-west-1', 'us-gov-east-1'] as const;
export const AWS_COMMERCIAL_REGIONS = ['us-east-1', 'us-east-2', 'us-west-2'] as const;
export const AWS_REGIONS = [...AWS_GOV_REGIONS, ...AWS_COMMERCIAL_REGIONS] as const;
export const AwsRegion = z.enum(AWS_REGIONS);
export type AwsRegion = z.infer<typeof AwsRegion>;
export function awsRegionsAllowed(commercialAllowed: boolean): readonly AwsRegion[] {
  return commercialAllowed ? AWS_REGIONS : AWS_GOV_REGIONS;
}
/**
 * Which Key Vault version of a provider's key MyRewards uses (APP-107 §2.2): set only by a save
 * that passed its test, so a key that fails never replaces the one in use. No secret in it.
 */
export const aiKeyRef = z
  .object({
    version: z.string().regex(/^[0-9A-Za-z-]{1,80}$/),
    createdAt: z.string().refine((v) => !Number.isNaN(Date.parse(v))),
  })
  .strict();
export type AiKeyRef = z.infer<typeof aiKeyRef>;
export type AiKeyVersions = Readonly<Record<AiProviderKind, AiKeyRef | null>>;
/**
 * A provider's saved key version as read: a version, nothing saved yet (null: the latest key is
 * used), or a stored entry that can't be read, which keeps AI closed for that provider rather than
 * quietly using the latest key.
 */
export type AiKeyVersionEntry = AiKeyRef | null | 'unreadable';
export const previewAccessSchema = z
  .object({
    enabled: z.boolean(),
    people: z
      .array(
        z
          .object({
            personId: text,
            endsOn: z
              .string()
              .refine((value) => parsePlainDate(value).ok)
              .nullable(),
          })
          .strict(),
      )
      .refine((people) => new Set(people.map((p) => p.personId)).size === people.length),
  })
  .strict();
export type PreviewAccess = z.infer<typeof previewAccessSchema>;
const setting = (
  schema: z.ZodType,
  defaultValue: unknown,
  scope: SettingScope,
  view: readonly Role[],
  edit: readonly Role[],
  extra: Partial<
    Pick<
      SettingDefinition,
      | 'effectiveDated'
      | 'reasonRequired'
      | 'versionRequired'
      | 'fixed'
      | 'reasonOnDelete'
      | 'managedOnAward'
      | 'customerInput'
      | 'portable'
    >
  > = {},
): SettingDefinition => ({
  schema,
  default: defaultValue,
  scope,
  view: [...new Set([...view, ...edit])],
  edit,
  effectiveDated: false,
  reasonRequired: false,
  ...extra,
});

// System Admin access is added by can(), except to fixed workflow references.
// Null defaults represent configuration that has not been supplied, not zero.
export const settingRegistry = {
  'budget.pools': setting(
    z.array(
      z
        .object({
          id: text,
          name: text,
          kind: z.enum(['sector', 'executive', 'paa_taa', 'spot']),
          sectorId: text.nullable(),
        })
        .strict(),
    ),
    [],
    'system',
    [S],
    [E, F, S],
  ),
  'budget.yearly': setting(cashOptions.nullable(), null, 'pool', [S, L], [E, F, S]),
  'budget.adjustment': setting(
    cashOptions.extend({ direction: z.enum(['addition', 'reduction']) }).nullable(),
    null,
    'pool',
    [S, L],
    [E, F, S],
    { reasonOnDelete: true },
  ),
  'budget.overageTopUp': setting(cashOptions.nullable(), null, 'pool', [L, F], [E]),
  'budget.move': setting(
    cashOptions
      .extend({ fromPoolId: text, toPoolId: text })
      .refine((v) => v.fromPoolId !== v.toPoolId)
      .nullable(),
    null,
    'system',
    [F, S],
    [E, S],
  ),
  'taa.defaultSplit': setting(rate, { num: 1, den: 2 }, 'system', [S, F], [E, S], {
    portable: true,
  }),
  'taa.poolAssignment': setting(z.record(text, text), {}, 'awardType', [L, F], [E, S], {
    managedOnAward: true,
  }),
  'fiscalYear.start': setting(
    z
      .object({ month: z.number().int().min(1).max(12), day: z.number().int().min(1).max(28) })
      .strict(),
    { month: 1, day: 1 },
    'system',
    ['employee'],
    [A, S],
    { portable: true },
  ),
  // APP-096: Areté's FMV is theirs to enter. No placeholder price: unset, it reads as not set and
  // an award that needs it can't be worked out.
  'values.fmvCents': setting(positive, null, 'system', [F, P], [E, S], {
    effectiveDated: true,
    customerInput: true,
  }),
  'values.cashShare': setting(rate, { num: 2, den: 3 }, 'awardType', [S, L, P], [E, F, S], {
    effectiveDated: true,
    portable: true,
  }),
  // APP-151 (Tom, 2026-10-24): the CFO's yearly value of one option, worked out from the FMV each
  // year (the 2026 figure is $53.00). PAA options are counted in it. Areté's to enter, like FMV.
  'values.optionValueCents': setting(positive, null, 'system', [S, E, F, L, P], [S], {
    effectiveDated: true,
    customerInput: true,
    reasonRequired: true,
  }),
  // APP-151 (Tom, 2026-10-24): a PAA's options are worth this share of its cash, at the value of
  // one option.
  // "That little algorithm", so the Stock Admin team (and System Admins) can change it; each award
  // keeps the share it was worked out with.
  'values.paaOptionShare': setting(rate, { num: 1, den: 2 }, 'awardType', [S, E, F, L, P], [S], {
    effectiveDated: true,
    reasonRequired: true,
    portable: true,
  }),
  'values.splitOverride': setting(rate, { num: 2, den: 3 }, 'awardType', [L, F], [E, S], {
    reasonRequired: true,
    managedOnAward: true,
  }),
  'coding.accounts': setting(
    z.object({ discretionary: ohGa, paa_taa: ohGa, spot: ohGa }).strict(),
    {
      discretionary: { OH: '0535-010', GA: '0713-010' },
      paa_taa: { OH: '0535-006', GA: '0713-006' },
      spot: { OH: '0535-001', GA: '0713-001' },
    },
    'system',
    [E, P],
    [F, S],
  ),
  'coding.poolProjects': setting(ohGa.nullable(), null, 'pool', [E, P, L], [F, S]),
  'coding.orgRule': setting(
    z.discriminatedUnion('kind', [
      z.object({ kind: z.literal('home') }).strict(),
      z.object({ kind: z.literal('fixed'), org: text }).strict(),
    ]),
    { kind: 'home' },
    'awardType',
    [E, P],
    [F, S],
    { portable: true },
  ),
  'coding.lineOverride': setting(z.enum(['OH', 'GA']).nullable(), null, 'person', [F], [S], {
    managedOnAward: true,
  }),
  'sector.programs': setting(
    z.array(z.object({ id: text, slug: text, name: text, retired: z.boolean() }).strict()),
    [],
    'sector',
    [E, S, F],
    [L],
  ),
  'sector.delegates': setting(
    z.array(
      z
        .object({
          personId: text,
          awardTypes: z.array(z.enum(['SDB', 'PAA', 'TAA'])),
          needsApproval: z.boolean(),
          active: z.boolean(),
        })
        .strict(),
    ),
    [],
    'sector',
    [E, S],
    [L],
  ),
  'awards.minOptionsPerPerson': setting(positive, 100, 'awardType', [L, D, S], [E, S], {
    portable: true,
  }),
  // APP-274: the delegates themselves live in `executive_delegations`; this carries who may view
  // and edit. The manager keeps an Executive to their own list.
  'executive.delegates': setting(
    z.array(
      z
        .object({
          executiveId: text,
          personId: text,
          awardTypes: z.array(z.enum(['EDB', 'SDB', 'PAA', 'TAA'])),
          needsApproval: z.boolean(),
          active: z.boolean(),
        })
        .strict(),
    ),
    [],
    'system',
    [E, S],
    [E],
  ),
  'election.choices': setting(
    z.tuple([z.literal('a'), z.literal('b'), z.literal('c'), z.literal('d')]),
    ['a', 'b', 'c', 'd'],
    'awardType',
    [A, S],
    [],
    { fixed: true },
  ),
  'election.windowDays': setting(z.number().int().min(1).max(3660), 7, 'awardType', [E, P], [S], {
    portable: true,
  }),
  'election.reminderDaysBefore': setting(
    z.array(z.number().int().min(0).max(3660)),
    [3, 0],
    'awardType',
    [E, P],
    [S],
    { portable: true },
  ),
  'election.managerNudge': setting(
    z.object({ enabled: z.boolean(), daysLate: positive }).strict(),
    { enabled: false, daysLate: 3 },
    'awardType',
    [E],
    [S],
    { portable: true },
  ),
  'election.choiceBPayment': setting(z.literal('manual'), 'manual', 'awardType', [S, P], [], {
    fixed: true,
  }),
  'letters.optionVestingYears': setting(positive, 4, 'awardType', ['employee'], [S], {
    portable: true,
  }),
  'letters.templates': setting(
    z.array(
      z.object({ templateId: text, signerId: text.nullable(), retired: z.boolean() }).strict(),
    ),
    [],
    'awardType',
    [E],
    [S],
  ),
  'letters.defaultRecipient': setting(
    z.enum(['requester', 'manager', 'employee']),
    'requester',
    'awardType',
    [L, E],
    [S],
    { portable: true },
  ),
  'directory.executives': setting(names, [], 'system', [E, S], [A]),
  'directory.sectorLeads': setting(recordStrings, {}, 'system', ['employee'], [A]),
  'directory.roleGrants': setting(
    z.array(
      z
        .object({
          personId: text,
          role: z.enum([
            'stock_admin',
            'payroll',
            'finance',
            'system_admin',
            'paa_taa_approver',
            'spot_reviewer',
            'support',
          ]),
        })
        .strict(),
    ),
    [],
    'system',
    [S],
    [A],
  ),
  'directory.appFields': setting(
    z
      .object({
        sectorId: text.nullable(),
        homeOrg: text.nullable(),
        costType: z.enum(['OH', 'GA']),
      })
      .strict(),
    { sectorId: null, homeOrg: null, costType: 'OH' },
    'person',
    [E, F],
    [S],
  ),
  'directory.sync': setting(
    z.object({ enabled: z.boolean(), schedule: text, employeesOnly: z.boolean() }).strict(),
    { enabled: true, schedule: 'nightly', employeesOnly: true },
    'system',
    [S],
    [A],
  ),
  // APP-113: which accounts the sync brings in, how it connects existing records, and whether a
  // first sign-in creates a profile while the sync isn't deciding who's in.
  'directory.syncFilters': setting(syncFiltersSchema, DEFAULT_SYNC_FILTERS, 'system', [S], [A], {
    reasonRequired: true,
  }),
  'directory.matching': setting(
    matchingSchema,
    { rules: DEFAULT_MATCH_RULES },
    'system',
    [S],
    [A],
    { reasonRequired: true },
  ),
  'directory.firstSignIn': setting(
    firstSignInSchema,
    { createProfile: true, beforeFirstSync: false },
    'system',
    [S],
    [A],
    {
      reasonRequired: true,
    },
  ),
  'directory.fieldOwnership': setting(
    z.record(text, z.enum(['entra', 'app', 'csv'])),
    {},
    'system',
    [S],
    [A],
  ),
  'directory.customFields': setting(
    z
      .array(
        z
          .object({ key: text, label: text, type: z.enum(['text', 'number', 'date', 'boolean']) })
          .strict(),
      )
      .refine((rows) => new Set(rows.map((r) => r.key)).size === rows.length),
    [],
    'system',
    [S],
    [A],
  ),
  // APP-148 D1: whether the Stock Admin team may act for a named Executive, and for a sector's
  // Sector Lead. On by default; System Admins turn one off, with a reason, in Roles & lists. What
  // was already done stays as recorded.
  'actFor.executives': setting(z.boolean(), true, 'system', [S, E], [A], { reasonRequired: true }),
  'actFor.sectorLeads': setting(z.boolean(), true, 'system', [S, E], [A], { reasonRequired: true }),
  'awards.enabled': setting(
    z.object({ enabled: z.boolean(), phase: positive }).strict(),
    { enabled: true, phase: 1 },
    'awardType',
    ['employee'],
    [A, S],
    { portable: true },
  ),
  'awards.recognizeAudience': setting(
    RecognizeAudienceSchema,
    { everyone: false, roles: [], personIds: [] },
    'awardType',
    [A],
    [A],
    { reasonRequired: true },
  ),
  'awards.displayName': setting(text.nullable(), null, 'awardType', ['employee'], [A, S], {
    portable: true,
  }),
  // K22/K37 supersede the old Coming soon page. Approver/funding stay configurable.
  'spot.enabled': setting(z.boolean(), true, 'system', ['employee'], [A, S], { portable: true }),
  'awards.groupSize': setting(
    z
      .object({ paaMin: positive, paaMax: positive, taaMin: positive })
      .strict()
      .refine((v) => v.paaMin <= v.paaMax && v.taaMin === v.paaMax + 1),
    { paaMin: 1, paaMax: 5, taaMin: 6 },
    'system',
    [E, S],
    [A, S],
    { portable: true },
  ),
  // APP-194: the Stock Admin team reads it, and turns each notice's email on or off as Send in
  // Settings › Messages (`notifications.email_switch`, Tom 2026-10-26); the rest stays a System
  // Admin's (APP-123), on System administration › Notifications.
  'notifications.events': setting(
    // SENT-076: bounded, so nothing written here (or kept in a refusal) can grow without end.
    z
      .record(text.max(64), z.object({ email: z.boolean(), inApp: z.boolean() }).strict())
      .refine((v) => Object.keys(v).length <= 100, { message: 'At most 100 notices.' }),
    {},
    'system',
    [E, S],
    [A],
    { portable: true },
  ),
  'notifications.executiveAddress': setting(z.email().nullable(), null, 'system', [E], [A]),
  'notifications.requiredEmails': setting(
    names,
    ['election', 'award_announced', 'delegate_entry', 'approval_needed'],
    'system',
    ['employee'],
    [A],
    { portable: true },
  ),
  // N3 b and M5 (APP-092): the Stock Admin daily digest, weekdays at the organization-local hour,
  // and whether the queue's request emails go one by one or only in the digest. M4: the digest
  // is built, off by default; M5's default is one email per request.
  'notifications.stockAdminDigest': setting(
    z
      .object({
        enabled: z.boolean(),
        hour: z.number().int().min(5).max(11),
        queueEmails: z.enum(['per_request', 'digest']),
      })
      .strict()
      .refine((v) => v.enabled || v.queueEmails === 'per_request', {
        message: 'The queue’s emails can go only in the digest while the digest is on.',
      }),
    { enabled: false, hour: 7, queueEmails: 'per_request' },
    'system',
    [S],
    [A],
    { portable: true },
  ),
  // APP-189: people and addresses copied on every email MyRewards sends. Its truth is the
  // `mail_copies` table, changed in Settings › Messages; this key carries who views and edits it
  // (the Stock Admin team, and System Admins through can()) and reads as that table. Never in a
  // starting setup file: it names people and is a mail setting.
  'notifications.copyTo': setting(
    z
      .array(
        z.union([
          z.object({ personId: z.uuid(), mode: z.enum(['cc', 'bcc']).optional() }).strict(),
          z
            .object({
              address: z.string().trim().min(3).max(254),
              mode: z.enum(['cc', 'bcc']).optional(),
            })
            .strict(),
        ]),
      )
      .max(20),
    [],
    'system',
    [S],
    [S],
  ),
  // APP-194: Settings › Messages' Copy switches: whether the copy list is copied on each email, as
  // the switches changed from their defaults (on for automatic emails and the Help hand-off, off
  // for the assistant's replies). Feedback is never copied, so it has no key. Not portable: it
  // goes with the copy list, which names people.
  'notifications.copyEmails': setting(
    z
      .record(z.string(), z.boolean())
      .refine((v) => Object.keys(v).every((k) => COPY_SWITCH_KEYS.includes(k)), {
        message: 'That isn’t an email the copy list can be on.',
      }),
    {},
    'system',
    [S],
    [S],
  ),
  'shareworks.claimTimeoutMinutes': setting(positive, 120, 'system', [S], [S], {
    portable: true,
  }),
  'payroll.earningStatementRequired': setting(z.boolean(), true, 'system', [P, S], [A, S], {
    portable: true,
  }),
  // Q18 defaults; Spot remains excluded until K37 is decided.
  'insights.repeatRecipient': setting(
    z.object({ awardCount: positive, valueCents: positive, rollingMonths: positive }).strict(),
    { awardCount: 3, valueCents: 500000, rollingMonths: 12 },
    'system',
    [E, L, S],
    [A, S],
    { portable: true },
  ),
  'org.timeZone': setting(
    text.refine((value) => {
      try {
        new Intl.DateTimeFormat('en', { timeZone: value }).format(0);
        return true;
      } catch {
        return false;
      }
    }),
    'America/Los_Angeles',
    'system',
    ['employee'],
    [A],
    { portable: true },
  ),
  // K37, APP-264 (Tom, 2026-11-01: "we need a separate pool for that"): the Spot Awards pool by
  // default; a saved choice keeps applying until it's changed (`SpotFundingSchema`).
  'spot.fundingPool': setting(
    SpotFundingSchema,
    DEFAULT_SPOT_FUNDING,
    'system',
    [E, S, F],
    [A, S],
    { portable: true },
  ),
  // APP-123: a projection onto configured spot.workflow.cashRange; never grants roster edits.
  // APP-268 D2: System Admins change it here now that the whole-workflow form has gone. Naming
  // them states what `settingAccess` already allows; it grants nothing new.
  'spot.cashRange': setting(SpotCashRangeSchema, null, 'system', [E, S, F], [A, S], {
    customerInput: true,
    reasonRequired: true,
    // APP-220 (SENT-125): written whole from a copy, like SENT-042; see spot.workflow.
    versionRequired: true,
  }),
  // APP-220 (Tom, 2026-10-27): a projection onto spot.workflow.committeeIds, so the Stock Admin
  // team, Executives and System Admins set the committee's members and nothing else. The first
  // save on an unset workflow carries today's route and cash range forward unchanged.
  'spot.committee': setting(SpotCommitteeSchema, [], 'system', [E, S], [A, S, E], {
    reasonRequired: true,
    versionRequired: true,
  }),
  'spot.reviewers': setting(names, [], 'system', [E, S], [A]),
  // APP-102: null preserves legacy authority until the explicit roster is confirmed. It decides
  // who approves Spot money, so the server needs the reason the Settings screen asks for.
  'spot.workflow': setting(SpotWorkflowConfigSchema.nullable(), null, 'system', [E, S], [A], {
    reasonRequired: true,
    // APP-220 (SENT-125): three editors' forms share this one stored workflow, so every write says
    // which saved copy it was made from (SENT-042), and a page left open never undoes a change.
    versionRequired: true,
  }),
  'preview.access': setting(
    previewAccessSchema,
    { enabled: false, people: [] },
    'system',
    [A],
    [A],
  ),
  'system.splash': setting(z.enum(['video', 'photo_wall']), 'video', 'system', ['employee'], [A]),
  // The footer's "Awards policy" link: Areté's own copy of the policy, wherever HR keeps it. Unset,
  // the footer leaves the link out rather than sending people to a page that isn't there.
  'org.awardsPolicyUrl': setting(
    z.url({ protocol: /^https$/, error: 'Use an https:// link.' }).nullable(),
    null,
    'system',
    ['employee'],
    [A, S],
    { portable: true },
  ),
  // The day MyRewards went live for everyone (FR5). Set once, by Go live on the go-live checklist,
  // which checks every required step first; the generic settings write refuses it.
  'org.launchDate': setting(
    z
      .string()
      .refine((value) => parsePlainDate(value).ok)
      .nullable(),
    null,
    'system',
    ['employee'],
    [A],
    { reasonRequired: true },
  ),
  'ai.enabled': setting(z.boolean(), false, 'system', [A], [A]),
  'ai.provider': setting(z.enum(AI_PROVIDER_KINDS), 'anthropic', 'system', [A], [A]),
  'ai.defaultModel': setting(text.nullable(), null, 'system', [A], [A]),
  // APP-107: Bedrock's region, which its endpoint follows. Written only by Admin › AI's tested save.
  'ai.bedrock': setting(
    z.object({ region: AwsRegion }).strict(),
    { region: 'us-gov-west-1' },
    'system',
    [A],
    [A],
  ),
  // APP-107: the vault version of each provider's key in use. Written only by Admin › AI's tested save.
  'ai.keyVersions': setting(
    z.object({ anthropic: aiKeyRef.nullable(), bedrock_govcloud: aiKeyRef.nullable() }).strict(),
    { anthropic: null, bedrock_govcloud: null },
    'system',
    [A],
    [A],
  ),
  // SYS7 (APP-048): one row per feature, off until a System Admin ticks it. `audience` narrows who
  // may use it on top of the feature's own permission (everyone, roles or named people, APP-139);
  // `model` overrides the default model.
  'ai.features': setting(
    z
      .object({
        help: aiFeature,
        email: aiFeature,
        teams: aiFeature,
        ask_data: aiFeature,
        // APP-104: added after the first release, so a stored value without it reads as off.
        feedback_summary: aiFeature.default(offForEveryone),
        // APP-104 §7: Voice for feedback, backed by Azure AI Speech, not a model (`model` stays null).
        feedback_voice: aiFeature.default(offForEveryone),
        // APP-124: Voice for Help chat, Azure AI Speech like feedback's; added later, so a stored
        // value without it reads as off.
        help_voice: aiFeature.default(offForEveryone),
        // APP-138 §D6: Feedback by email and Teams; added later, so a stored value without it reads as off.
        feedback_assistant: aiFeature.default(offForEveryone),
        // APP-263 (APP-191 D10 C): Ask the data's Automated summary, which sends an answer's totals
        // (never people's names or ids) to the model, and only while Ask the data is on too. Added later, so
        // a stored value without it reads as off, for the Executives. In production it stays off
        // until Areté IT agrees (APP-171's open question).
        ask_summary: aiFeature.default(offForExecutives),
      })
      .strict(),
    {
      help: offForEveryone(),
      email: { on: false, audience: LEGACY_AI_AUDIENCES.leaders(), model: null },
      teams: offForEveryone(),
      ask_data: {
        on: false,
        audience: { ...ASK_DATA_DEFAULT_AUDIENCE, roles: [...ASK_DATA_DEFAULT_AUDIENCE.roles] },
        model: null,
      },
      feedback_summary: offForEveryone(),
      feedback_voice: offForEveryone(),
      help_voice: offForEveryone(),
      feedback_assistant: offForEveryone(),
      ask_summary: offForExecutives(),
    },
    'system',
    [A],
    [A],
    { versionRequired: true },
  ),
  // Requests per person per organization day, across every AI feature (SYS7 default 50, to confirm).
  'ai.dailyLimit': setting(positive.max(10_000), 50, 'system', [A], [A]),
  // Help history kept for its person (AI4 default 30 days, to confirm). Ask the data had it too
  // until APP-258, which gave Ask the data its own months (APP-191 D8, Q9).
  'ai.historyDays': setting(positive.max(3650), 30, 'system', [A], [A]),
  // APP-258 (APP-191 D8, Q9, Tom "defaults OK" 2026-10-27): Ask the data conversations are kept
  // this many months after their last question unless their person deletes them; then purged.
  'ai.askDataHistoryMonths': setting(positive.max(120), 24, 'system', [A], [A]),
  // The Stock Admin team's shared mailbox, shown in Help next to the team (APP-048). Optional:
  // without it, Help offers a message sent through MyRewards to everyone on the team.
  'help.teamEmail': setting(z.email().max(254).nullable(), null, 'system', ['employee'], [A]),
  // WP-FEEDBACK (APP-104 §7): the master switch, who may send, and how long evidence is kept.
  'feedback.enabled': setting(z.boolean(), true, 'system', ['employee'], [A], {
    reasonRequired: true,
  }),
  'feedback.audience': setting(
    AudiencePolicy,
    { everyone: true, roles: [], personIds: [] },
    'system',
    [A],
    [A],
    { reasonRequired: true },
  ),
  'feedback.evidenceDays': setting(positive.max(3650), 90, 'system', [A], [A]),
  // The dictation tip under the words box: a pointer to the operating system's own dictation.
  'feedback.dictationTip': setting(z.boolean(), true, 'system', ['employee'], [A], {
    reasonRequired: true,
  }),
  // APP-138 §D18: where Email in Outlook and Chat in Teams open, for reviewers reaching a sender:
  // Microsoft 365's own web addresses only (SENT-026), each field its own app's. Null follows the
  // cloud MyRewards runs in (the server's Teams client; Outlook's commercial, GCC High or DoD
  // address).
  'feedback.contactLinks': setting(
    z
      .object({
        outlook: microsoftOrigin(
          CONTACT_LINK_HOSTS.outlook,
          'Use Outlook on the web from Microsoft 365, such as https://outlook.office365.us.',
        ),
        teams: microsoftOrigin(
          CONTACT_LINK_HOSTS.teams,
          'Use Teams on the web from Microsoft 365, such as https://gov.teams.microsoft.us.',
        ),
      })
      .strict()
      .nullable(),
    null,
    'system',
    [A],
    [A],
  ),
  'my.preferredName': setting(z.string().trim().max(120), '', 'person', ['employee'], ['employee']),
  // SET1 (APP-081): optional, self-entered pronouns. Like the preferred name, the value lives on
  // the person record (migration 0035); this key only says who may view and edit it.
  'my.pronouns': setting(
    z.enum(['she/her', 'he/him', 'they/them']).nullable(),
    null,
    'person',
    ['employee'],
    ['employee'],
  ),
  // SET1 (APP-081): where sign-in lands, one of the person's own Level 1 items (Home when it
  // isn't one of theirs any more). A link someone followed still opens its own page. APP-236:
  // Manage is one. 'executive' stays valid for the rows saved before the Executive pages moved into
  // Manage, which read as Manage (`startPageFor`); the contract's StartPage no longer offers it, so
  // nothing writes it now.
  'my.startPage': setting(
    z.enum(['home', 'my-rewards', 'recognize', 'manage', 'insights', 'executive', 'admin']),
    'home',
    'person',
    ['employee'],
    ['employee'],
  ),
  // APP-236 §4 (Tom 2026-10-29, P1 = b): the Manage group that comes first and that Manage opens on,
  // or null for Automatic (from the roles the person holds). A group they no longer have is kept
  // and ignored until they have it again (`manageLayoutFor`).
  'my.mainWork': setting(
    z
      .enum(['executive', 'stock_admin', 'payroll', 'sector_lead', 'approver', 'committee', 'team'])
      .nullable(),
    null,
    'person',
    ['employee'],
    ['employee'],
  ),
  // SET1 (APP-092): per event, email and in app, and Teams for the notices posted there (AI3).
  'my.notifications': setting(
    z.record(
      text,
      z.object({ email: z.boolean(), inApp: z.boolean(), teams: z.boolean().optional() }).strict(),
    ),
    {},
    'person',
    ['employee'],
    ['employee'],
  ),
  'my.hero': setting(z.enum(['full', 'slim']), 'full', 'person', ['employee'], ['employee']),
  'my.motion': setting(
    z.enum(['system', 'reduced']),
    'system',
    'person',
    ['employee'],
    ['employee'],
  ),
  'my.accessibility': setting(
    z
      .object({ contrast: z.enum(['standard', 'high']), textSize: z.enum(['standard', 'large']) })
      .strict(),
    { contrast: 'standard', textSize: 'standard' },
    'person',
    ['employee'],
    ['employee'],
  ),
  // Getting-started steps the person dismissed from Waiting on you (K25 "until done or
  // dismissed"); My settings shows them again (APP-054).
  'my.gettingStarted': setting(
    z
      .object({
        dismissed: z
          // APP-076: the training is a getting-started step too (K11, K25).
          .array(z.enum(['preferences', 'training']))
          .refine((steps) => new Set(steps).size === steps.length),
      })
      .strict(),
    { dismissed: [] },
    'person',
    ['employee'],
    ['employee'],
  ),
  // APP-135: rows per page, by table id (the ids live in @myrewards/contracts' TableId); a table
  // not named uses its own starting size. Writes merge into the stored value.
  'my.tablePageSizes': setting(
    z.record(text, z.union([z.literal(25), z.literal(50), z.literal(100), z.literal(200)])),
    {},
    'person',
    ['employee'],
    ['employee'],
  ),
  // APP-149 D7: the order Waiting on you opens in on Home. A sort chosen there lasts the visit.
  'my.waitingOrder': setting(
    z.enum(['oldest', 'newest', 'role']),
    'oldest',
    'person',
    ['employee'],
    ['employee'],
  ),
  // APP-149 D7: every table opens in its default order ('start'), or as the person last sorted it.
  'my.tableSortMode': setting(
    z.enum(['start', 'keep']),
    'start',
    'person',
    ['employee'],
    ['employee'],
  ),
  // APP-149 D7: the last sort, by table id (the ids live in @myrewards/contracts' SortTableId),
  // used only with 'keep'. Writes merge into the stored value, like the rows per page.
  'my.tableSorts': setting(
    z.record(
      text,
      z
        .object({
          column: z.string().regex(/^[a-z][A-Za-z0-9_]{0,39}$/),
          dir: z.enum(['asc', 'desc']),
        })
        .strict(),
    ),
    {},
    'person',
    ['employee'],
    ['employee'],
  ),
} as const satisfies Readonly<Record<string, SettingDefinition>>;
export type SettingKey = keyof typeof settingRegistry;
/** Every registry key, in registry order. */
export function settingKeys(): SettingKey[] {
  return Object.keys(settingRegistry) as SettingKey[];
}
/** The keys a starting setup file's `settings` block may carry (APP-118 D10), in registry order. */
export function portableSettingKeys(): SettingKey[] {
  return settingKeys().filter((k) => getSetting(k)?.portable === true);
}
/** Prefixes of the settings D10 never lets travel: AI, Teams, mail, directory and personal. */
const NOT_PORTABLE_PREFIXES = ['ai.', 'teams.', 'mail.', 'directory.', 'my.', 'help.'] as const;
/** Why a key marked portable breaks D10's rule, or null (the registry's tests run it on each). */
export function portableRuleProblem(key: string): string | null {
  const definition = getSetting(key);
  if (definition === undefined) return 'not in the registry';
  if (definition.portable !== true) return null;
  if (NOT_PORTABLE_PREFIXES.some((prefix) => key.startsWith(prefix)))
    return 'an AI, Teams, mail, directory or personal setting';
  if (key === 'org.launchDate') return 'the go-live date';
  if (definition.managedOnAward === true) return 'managed on awards';
  if (definition.fixed === true) return 'a fixed rule nobody edits';
  if (definition.customerInput === true) return 'a customer input with its own section';
  if (definition.scope === 'person') return 'scoped to a person';
  if (definition.edit.length === 0) return 'nobody edits it';
  return null;
}
export function getSetting(key: string): SettingDefinition | undefined {
  return Object.hasOwn(settingRegistry, key) ? settingRegistry[key as SettingKey] : undefined;
}
export function validateSetting(key: string, value: unknown): Result<unknown> {
  const definition = getSetting(key);
  if (!definition) return failure('unknown_setting');
  const parsed = definition.schema.safeParse(value);
  return parsed.success
    ? success(parsed.data)
    : {
        ok: false,
        issues: parsed.error.issues.map((issue) => ({
          code: 'invalid_setting_value',
          field: issue.path.map(String).join('.'),
        })),
      };
}
export interface EffectiveValue<T> {
  readonly effectiveFrom: string;
  readonly value: T;
}
export function valueAt<T>(history: readonly EffectiveValue<T>[], date: string): Result<T> {
  if (!parsePlainDate(date).ok) return failure('invalid_date');
  const dates = new Set<string>();
  let selected: EffectiveValue<T> | undefined;
  for (const item of history) {
    if (!parsePlainDate(item.effectiveFrom).ok) return failure('invalid_effective_date');
    if (dates.has(item.effectiveFrom)) return failure('duplicate_effective_date');
    dates.add(item.effectiveFrom);
    if (item.effectiveFrom <= date && (!selected || item.effectiveFrom > selected.effectiveFrom))
      selected = item;
  }
  return selected ? success(selected.value) : failure('no_effective_value');
}
export interface NotificationPreference {
  readonly email: boolean;
  readonly inApp: boolean;
}
export function effectiveNotifications(
  preferences: Readonly<Record<string, NotificationPreference>>,
  requiredEmails: readonly string[],
): Readonly<Record<string, NotificationPreference & { readonly emailRequired: boolean }>> {
  return Object.fromEntries(
    [...new Set([...Object.keys(preferences), ...requiredEmails])].map((key) => {
      const required = requiredEmails.includes(key),
        preference = preferences[key];
      return [
        key,
        {
          email: required || (preference?.email ?? true),
          inApp: preference?.inApp ?? true,
          emailRequired: required,
        },
      ];
    }),
  );
}
/** A feature may send data to a model only with the master switch, its own switch and a tested key. */
export function aiFeatureEnabled(
  settings: {
    readonly master: boolean;
    readonly features: Readonly<Record<AiFeature, { readonly on: boolean }>>;
    readonly credentialsReady: boolean;
  },
  feature: AiFeature,
): boolean {
  return settings.master && settings.features[feature].on && settings.credentialsReady;
}
